/** Deputy pages from the ☰ menu: Stats, Resources, My signature (and the papers-issued box used in Stats). */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { fmtDateTime } from '../shared/format.ts';
import { MySignatureCard, NotaryCard } from '../shared/MySignature.tsx';
import { PasskeysCard } from '../shared/passkey.tsx';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { Card, Empty, ErrorBox, Notice, Spinner, StatusBadge, useLoad } from '../shared/ui.tsx';

type Range = 'last7' | 'last30' | 'last365';
const RANGE_LABEL: Record<Range, string> = { last7: '7 days', last30: '30 days', last365: '12 months' };
const OUTCOME: Record<string, string> = { served: 'Served', non_served: 'Non-served', open: 'Still open', recalled: 'Recalled', other: 'Returned / closed' };
const typeName = (code: string) => code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);
const num = (n: number | null | undefined, unit = '') => (n == null ? '—' : `${n}${unit}`);
const hm = (min: number) => (min >= 60 ? `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m` : `${min}m`);

function RangeSeg({ range, onChange, options = ['last7', 'last30', 'last365'] }: { range: Range; onChange: (r: Range) => void; options?: Range[] }) {
  return (
    <div className="seg" role="group" aria-label="Period">
      {options.map((r) => (
        <button key={r} className={range === r ? 'active' : ''} onClick={() => onChange(r)}>
          {RANGE_LABEL[r]}
        </button>
      ))}
    </div>
  );
}

/** Papers issued to me in the period, filterable by how they turned out. */
export function IssuedCard({ range: fixed }: { range?: Range }) {
  const { navigate } = useRouter();
  const [own, setOwn] = useState<Range>('last30');
  const range = fixed ?? own;
  const [show, setShow] = useState<string | null>(null);
  const iss = useLoad(() => api.get<any>(`/api/me/issued?range=${range}`), [range]);
  const i = iss.data;
  const list = i ? (show ? i.papers.filter((p: any) => p.outcome === show) : i.papers) : [];
  const box = (key: string | null, n: number, label: string, tone = '') => (
    <button className={`hs ${tone} hs-btn ${show === key ? 'active' : ''}`} onClick={() => setShow(show === key ? null : key)} aria-pressed={show === key}>
      <span className="hs-n">{n}</span>
      <span className="hs-l">{label}</span>
    </button>
  );
  return (
    <Card title="Papers issued to me" actions={fixed ? undefined : <RangeSeg range={own} onChange={(r) => (setOwn(r), setShow(null))} options={['last30', 'last365']} />}>
      <ErrorBox error={iss.error} />
      {!i ? (
        <Spinner />
      ) : (
        <>
          <div className="home-stats issued-stats">
            {box(null, i.total, `Issued · ${RANGE_LABEL[range]}`, 'total')}
            {box('served', i.served, 'Served', 'ok')}
            {box('non_served', i.nonServed, 'Non-served', 'bad')}
            {box('open', i.open, 'Still open', 'info')}
            {i.recalled > 0 && box('recalled', i.recalled, 'Recalled')}
          </div>
          {i.transferred > 0 && (
            <p className="small muted">
              {i.fromOffice} from the office · {i.transferred} transferred from another deputy
            </p>
          )}
          {list.length === 0 ? (
            <Empty>No papers {show ? `(${OUTCOME[show]?.toLowerCase()}) ` : ''}issued to you in this period.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table issued-table">
                <thead>
                  <tr>
                    <th>Issued</th>
                    <th>Paper</th>
                    <th>Defendant</th>
                    <th>Now</th>
                  </tr>
                </thead>
                <tbody>
                  {list.slice(0, 300).map((p: any) => (
                    <tr key={p.id} onClick={() => navigate(`/field/paper/${p.id}`)} className="clickable">
                      <td className="small">
                        {fmtDateTime(p.issuedAt)}
                        {p.via === 'transfer' && <div className="muted">transfer</div>}
                      </td>
                      <td>
                        <span className="mono">{p.number}</span>
                        <div className="small muted">{typeName(p.paperType)}</div>
                      </td>
                      <td>{p.defendant}</td>
                      <td>
                        <StatusBadge status={p.status} />
                        <div className="small muted">{OUTCOME[p.outcome]}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {list.length > 300 && <p className="small muted">Showing the latest 300 of {list.length}.</p>}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** One horizontal bar row: label, bar (share of the max), value text. */
function Bars({ rows, unit, max }: { rows: { label: string; value: number | null; note?: string }[]; unit: string; max?: number }) {
  const top = max ?? Math.max(1, ...rows.map((r) => r.value ?? 0));
  return (
    <div className="bars">
      {rows.map((r) => (
        <div className="bar-row" key={r.label} title={`${r.label}: ${r.value == null ? 'no attempts' : `${r.value}${unit}`}${r.note ? ` (${r.note})` : ''}`}>
          <span className="bar-l">{r.label}</span>
          <span className="bar-track">
            <span className="bar-fill" style={{ width: `${r.value == null ? 0 : Math.max(r.value > 0 ? 2 : 0, (r.value / top) * 100)}%` }} />
          </span>
          <span className="bar-v">
            {r.value == null ? '—' : `${r.value}${unit}`}
            {r.note && <small> {r.note}</small>}
          </span>
        </div>
      ))}
    </div>
  );
}

function Tile({ n, label, note, tone = '' }: { n: string | number; label: string; note?: string; tone?: string }) {
  return (
    <div className={`hs ${tone}`}>
      <span className="hs-n">{n}</span>
      <span className="hs-l">{label}</span>
      {note && <span className="hs-c">{note}</span>}
    </div>
  );
}

export function StatsPage({ online }: { online: boolean }) {
  const [range, setRange] = useState<Range>('last30');
  const r = useLoad(() => (online ? api.get<any>(`/api/me/stats?range=${range}`) : Promise.resolve(null)), [range, online]);
  if (!online) return <Notice tone="warn">Stats need a connection.</Notice>;
  const s = r.data;
  const W = s?.attempts.windows;
  return (
    <div className="stack home">
      <div className="row between wrap gap">
        <h1>My activity</h1>
        <RangeSeg range={range} onChange={setRange} />
      </div>
      <ErrorBox error={r.error} />
      {!s ? (
        <Spinner />
      ) : (
        <>
          {s.roles?.patrol && s.patrol && (
            <Card title={`Patrol · ${RANGE_LABEL[range]}`}>
              <div className="home-stats">
                <Tile n={s.patrol.reports} label="Incident reports" note={s.patrol.unfinished ? `${s.patrol.unfinished} not finished` : 'submitted or approved'} tone={s.patrol.unfinished ? 'info' : ''} />
                <Tile n={s.patrol.open_cases} label="Open cases" note="yours, not cleared" />
                <Tile n={s.patrol.cleared} label="Cases cleared" tone="ok" />
                <Tile n={s.patrol.arrests} label="Arrests" />
                <Tile n={s.patrol.citations} label="Citations" />
                <Tile n={s.patrol.warnings} label="Written warnings" />
                <Tile n={s.patrol.tows} label="Vehicles towed" />
                <Tile n={s.patrol.evidence} label="Evidence collected" />
                <Tile n={s.patrol.supplements} label="Supplements" />
                <Tile n={s.patrol.warrant_requests} label="Warrants requested" />
                <Tile n={s.patrol.warrants_served} label="Warrants served" />
                <Tile n={s.patrol.court} label="Court appearances" />
              </div>
            </Card>
          )}

          {s.roles?.civil !== false && (
          <>
          <Card title="Right now">
            <div className="home-stats">
              <Tile n={s.now.held} label="Papers in my possession" note={s.now.toPickUp ? `${s.now.toPickUp} to pick up` : undefined} />
              <Tile n={s.now.attemptsToday} label="Attempts today" />
              <Tile n={s.now.affidavitsWaiting} label="Affidavits to sign" tone={s.now.affidavitsWaiting ? 'info' : ''} />
            </div>
          </Card>

          <Card title={`Service · ${RANGE_LABEL[range]}`}>
            <div className="home-stats">
              <Tile n={s.service.served} label="Papers served" note={`${s.service.personal} personal · ${s.service.substitute} substitute · ${s.service.posting} posted`} tone="ok" />
              <Tile n={pct(s.service.personalPct)} label="Personal service" note={`of ${s.service.completed} completed`} tone="ok" />
              <Tile n={pct(s.service.substitutePct)} label="Substitute / posting" tone="info" />
              <Tile n={pct(s.service.nonServedPct)} label="Non-service" note={`${s.service.nonServed} returned non-served`} tone="bad" />
              <Tile n={pct(s.service.servedOnTimePct)} label="Served before cutoff" note={s.service.servedWithDeadline ? `${s.service.servedWithDeadline} with a court date` : 'no court dates'} />
              <Tile n={num(s.service.avgAttemptsToServe)} label="Attempts per serve" note="average, all deputies’ attempts" />
              <Tile n={num(s.service.avgDaysIssueToService, ' d')} label="Issued → served" note="average days" />
              <Tile n={s.affidavitsSigned} label="Affidavits signed" />
            </div>
          </Card>

          <Card title={`Attempts · ${RANGE_LABEL[range]}`}>
            <div className="home-stats">
              <Tile n={s.attempts.total} label="Attempts" note={`on ${s.attempts.papers} papers`} />
              <Tile n={s.attempts.daysWorked} label="Days in the field" note={s.attempts.perDayWorked ? `${s.attempts.perDayWorked} attempts a day` : undefined} />
              <Tile n={s.attempts.doorHangers} label="Door hangers left" />
            </div>
            <div className="stats-2">
              <div>
                <h3>Serve rate by time of day</h3>
                <Bars
                  unit="%"
                  max={100}
                  rows={[
                    { label: 'M · 6a–12p', value: W.morning.rate, note: `${W.morning.served}/${W.morning.attempts}` },
                    { label: 'A · 12–6p', value: W.afternoon.rate, note: `${W.afternoon.served}/${W.afternoon.attempts}` },
                    { label: 'E · 6–10p', value: W.evening.rate, note: `${W.evening.served}/${W.evening.attempts}` },
                    { label: 'W · Saturday', value: W.weekend.rate, note: `${W.weekend.served}/${W.weekend.attempts}` },
                  ]}
                />
              </div>
              <div>
                <h3>Serve rate by day of week</h3>
                <Bars unit="%" max={100} rows={s.attempts.weekdays.map((d: any) => ({ label: d.name, value: d.rate, note: `${d.served}/${d.attempts}` }))} />
              </div>
              <div>
                <h3>How attempts ended</h3>
                {s.attempts.outcomes.length === 0 ? <p className="muted">No attempts.</p> : <Bars unit="" rows={s.attempts.outcomes.map((o: any) => ({ label: o.label, value: o.n }))} />}
              </div>
            </div>
            <p className="small muted">Rates = served ÷ attempts in that slot (served/attempts shown).</p>
          </Card>

          <IssuedCard range={range} />
          </>
          )}

          <Card title={`On duty · ${RANGE_LABEL[range]}`}>
            <div className="home-stats">
              <Tile n={num(s.duty.hours, ' h')} label="On duty" />
              <Tile n={num(s.duty.miles, ' mi')} label="Miles driven" note={s.roles?.civil !== false && s.duty.milesPerServe ? `${s.duty.milesPerServe} mi per serve` : undefined} />
              <Tile n={hm(s.duty.idleMinutes)} label="Idle" note={s.roles?.civil !== false ? 'parked 10+ min, not at a paper' : 'parked 10+ minutes'} />
              <Tile n={s.safety.ok} label="“I’m OK” check-ins" note={s.safety.alerts ? `${s.safety.alerts} dispatch alert(s)` : 'no dispatch alerts'} tone={s.safety.alerts ? 'bad' : ''} />
            </div>
            <p className="small muted">Miles and idle come from your location while on duty; miles are straight-line between fixes, so winding roads read a little short.</p>
          </Card>
        </>
      )}
    </div>
  );
}

export function ResourcesPage({ online }: { online: boolean }) {
  const { me } = useAuth();
  const home = useLoad(() => (online ? api.get<any>('/api/me/home') : Promise.resolve(null)), [online]);
  if (!online) return <Notice tone="warn">Resources need a connection.</Notice>;
  const f = home.data?.forms;
  const forms = f
    ? ([
        ['Equipment request', 'Radios, vests, supplies', f.equipment],
        ['Vehicle repair request', 'Something wrong with your unit', f.vehicleRepair],
        ['Notable / critical incident alert', 'Tell command about a serious incident', f.incident],
      ] as const)
    : [];
  return (
    <div className="stack home">
      <h1>Resources</h1>
      <ErrorBox error={home.error} />
      <Card title="Requests">
        {!f ? (
          <Spinner />
        ) : (
          <div className="res-grid">
            {forms.map(([label, hint, url]) =>
              url ? (
                <a key={label} className="res" href={url} target="_blank" rel="noreferrer">
                  <strong>{label} ↗</strong>
                  <small>{hint}</small>
                </a>
              ) : (
                <span key={label} className="res disabled" title="An administrator adds the form link under Administration → Agency settings">
                  <strong>{label}</strong>
                  <small>Not set up yet — ask an administrator</small>
                </span>
              ),
            )}
          </div>
        )}
      </Card>
      <Card title="Office">
        <p>
          <strong>{me?.agency.name}</strong>
        </p>
        {home.data?.contact?.phone && (
          <p>
            <a href={`tel:${home.data.contact.phone.replace(/[^\d+]/g, '')}`}>{home.data.contact.phone}</a>
          </p>
        )}
        {home.data?.contact?.address && <p className="muted">{home.data.contact.address}</p>}
      </Card>
    </div>
  );
}

export function SignaturePage() {
  return (
    <div className="stack home">
      <h1>My signature</h1>
      <p className="muted">Saved once, placed on affidavits when you sign with “use my saved signature”.</p>
      <MySignatureCard />
      <NotaryCard />
    </div>
  );
}

export function SecurityPage() {
  return (
    <div className="stack home">
      <h1>Sign-in &amp; unlock</h1>
      <PasskeysCard />
    </div>
  );
}
